import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useHistory, useLocation } from 'react-router-dom';
import TrashIcon from '@mui/icons-material/FolderDelete';

import { FOLDER_CONTENT_FILTER } from '../../../../enums/ENUMS';
import { FILE_ACCESS_TYPE, FOLDER_TYPE, VIDEOFEED } from '../../../../enums/TYPES';
import { getSharedFolderByIdThunk } from '../../../../store/videos/videoSlice';
import { switchSidebar } from '../../../../store/sharedStore/sharedStoreSlice';
import { AllContentVideofeedsHeader } from './AllContentVideofeedsHeader';
import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../../../enums/ACCESS_ENUM';
import { getFolderContents } from '../../../../api/videofeeds';
import LinearProgress from '../../../modules/LinearProgress';
import { SortOptions, SortType } from '../../../../enums/SORT';
import { useAppDispatch } from '../../../../store/hooks';
import { NOTIFICATION } from '../../../../enums/ENUMS';
import SearchInput from '../../../modules/SearchInput';
import styles from './AllContentVideofeeds.module.scss';
import AllContentVideo from './AllContentVideo';
import { getShallowFolder } from '../../../../api';
import {
  findSearchValue,
  getAllContentSortOptionAndType,
  setAllContentSortOptionAndType,
} from '../../../../helpers';
import { PATHS } from '../../../../enums';
import { PaginationFooter } from '../../../modules/PaginationFooter';
import useDebounce from '../../../../helpers/useDebounce';
import DeletedVideofeedsModal from '../../../VideofeedsList/DeletedVideofeedsModal';
import { OrderSelector } from '../../../modules/Selector';
import FolderContentRow from '../../../modules/Folders/FolderContentRow';
import { ContainedButton } from '../../../modules/Buttons';
import ShareFolderModal from '../../../SharingModals/MyFolderPage/ShareFolderModal';
import VideofeedShareModal from '../../../SharingModals/MyFolderPage/VideofeedShareModal';
import { selectShareFolder, switchShareModal } from '../../../../store/folders/foldersSlice';

export interface VIDEOFEED_WITH_SIZE extends VIDEOFEED {
  size: number;
  highest_user_access: FILE_ACCESS_TYPE;
}

type FOLDER_CONTENT_ITEM =
  | (FOLDER_TYPE & { item_type: 'folder' })
  | (VIDEOFEED_WITH_SIZE & { item_type?: 'videofeed' });

const CONTENT_FILTER_OPTIONS = [
  { label: 'All Items', value: FOLDER_CONTENT_FILTER.ALL, order: SortType.ASC },
  { label: 'Timelines', value: FOLDER_CONTENT_FILTER.TIMELINE, order: SortType.ASC },
  { label: 'Composers', value: FOLDER_CONTENT_FILTER.COMPOSER, order: SortType.ASC },
  { label: 'Subfolders', value: FOLDER_CONTENT_FILTER.SUBFOLDER, order: SortType.ASC },
];

const getContentFilter = (value?: string | null): FOLDER_CONTENT_FILTER => {
  switch (value) {
    case FOLDER_CONTENT_FILTER.TIMELINE:
    case FOLDER_CONTENT_FILTER.COMPOSER:
    case FOLDER_CONTENT_FILTER.SUBFOLDER:
      return value;
    default:
      return FOLDER_CONTENT_FILTER.ALL;
  }
};

const AllContentVideofeeds = () => {
  const [folderContents, setFolderContents] = useState<FOLDER_CONTENT_ITEM[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [filterBy, setFilterBy] = useState<ACCESS_TYPE_ENUM | null>();

  const notification = useSnackbar();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const folderId = searchParams.get('folderId');
  const folderTrail = searchParams.get('folderTrail');
  const breadcrumbAncestorIds = useMemo(
    () =>
      folderTrail
        ? decodeURIComponent(folderTrail).split(',').filter(Boolean)
        : [],
    [folderTrail],
  );
  const dispatch = useAppDispatch();

  const sortFromQuery = findSearchValue('sort');
  const directAccess = findSearchValue('directAccess');
  const folderSearch = findSearchValue('search');

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(SortType.ASC);
  const [sortOption, setSortOption] = useState(SortOptions.NAME);
  const [contentFilter, setContentFilter] = useState<FOLDER_CONTENT_FILTER>(
    () => getContentFilter(searchParams.get('contentFilter')),
  );

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [folder, setFolder] = useState<FOLDER_TYPE>();
  const [breadcrumbFolders, setBreadcrumbFolders] = useState<FOLDER_TYPE[]>([]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setContentFilter(getContentFilter(searchParams.get('contentFilter')));
  }, [location.search]);

  const fetchFolder = async (id: string) => {
    try {
      const res = await getShallowFolder(id);
      setFolder(res.data);
    } catch (err) {
      console.log(err);
      history.push(PATHS.ALL_CONTENT.path);
    }
  };

  const history = useHistory();

  useEffect(() => {
    if (sort === SortType.ASC) {
      setSort(SortType.ASC);
    } else if (sort === SortType.DESC) {
      setSort(SortType.DESC);
    }
  }, [sortFromQuery]);

  useEffect(() => {
    let active = true;

    const loadBreadcrumbFolders = async () => {
      if (!breadcrumbAncestorIds.length) {
        setBreadcrumbFolders([]);
        return;
      }

      try {
        const responses = await Promise.all(
          breadcrumbAncestorIds.map((id) => getShallowFolder(id)),
        );

        if (active) {
          setBreadcrumbFolders(responses.map((response) => response.data));
        }
      } catch (err) {
        if (active) {
          setBreadcrumbFolders([]);
        }
      }
    };

    loadBreadcrumbFolders();

    return () => {
      active = false;
    };
  }, [breadcrumbAncestorIds]);

  const debouncedSearch = useDebounce(search, 1000);

  const fetchFolderContents = async (
    sortFromArg?: SortType,
    sortOpinionFromArg?: SortOptions,
  ) => {
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    setLoading(true);
    try {
      if (folderId) {
        await fetchFolder(folderId);
      }
      const sortOptionObject = getAllContentSortOptionAndType();
      const activeSort = sortOptionObject
        ? (sortOptionObject.split('/')[0] as SortType)
        : sortFromArg || sort;
      const activeSortOption = sortOptionObject
        ? (sortOptionObject.split('/')[1] as SortOptions)
        : sortOpinionFromArg || sortOption;

      const res = await getFolderContents({
        folderId: folderId as string,
        sort: activeSort,
        sortOption: activeSortOption,
        accessType: filterBy ? filterBy : undefined,
        contentFilter,
        page,
        limit,
        search,
        view: 'all-content',
      });
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      setFolderContents(res.documents);
      setTotal(res.total);

      if (folderId && page && limit) {
        await dispatch(
          getSharedFolderByIdThunk({
            folderId,
            page: +page,
            limit: +limit,
            sort: activeSort,
            search: folderSearch || '',
            sortOption: activeSortOption,
            accessType: filterBy ? [filterBy] : undefined,
          }),
        );
      }
    } catch (err) {
      if ((err as any)?.response?.data?.message) {
        notification.showMessage((err as any).response.data.message);
        history.push(PATHS.ALL_CONTENT.path);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    } finally {
      setLoading(false);
    }
  };

  const urlParams = document.location.search;

  const [fetchTrigger, setFetchTrigger] = useState(0);
  const fetchDataCallback = () => {
    setFetchTrigger(Date.now());
  };
  useEffect(() => {
    const res = getAllContentSortOptionAndType();
    if (res) {
      const sortAndOption = res.split('/');
      setSort(sortAndOption[0] as SortType);
      setSortOption(sortAndOption[1] as SortOptions);
      fetchFolderContents(
        sortAndOption[0] as SortType,
        sortAndOption[1] as SortOptions,
      );
    } else {
      fetchFolderContents();
    }
  }, [
    urlParams,
    sort,
    sortOption,
    filterBy,
    contentFilter,
    debouncedSearch,
    page,
    limit,
    fetchTrigger,
  ]);

  useEffect(() => {
    dispatch(switchSidebar(false));
  }, [debouncedSearch]);

  const searchContent = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setSearch(e.target.value);
    setPage(0);
  };

  const sortFiltering = (value: ACCESS_TYPE_ENUM) => {
    if (value === filterBy || value === ACCESS_TYPE_ENUM.ALL) {
      setFilterBy(null);
    } else {
      setFilterBy(value);
    }
    setPage(0);
  };

  const onContentFilterSelect = (
    _: any,
    option?: { label: string; value: string; order: SortType },
  ) => {
    if (!option) return;
    const nextFilter = option.value as FOLDER_CONTENT_FILTER;
    const nextSearch = new URLSearchParams(location.search);

    nextSearch.set('page', '0');
    if (nextFilter === FOLDER_CONTENT_FILTER.ALL) {
      nextSearch.delete('contentFilter');
    } else {
      nextSearch.set('contentFilter', nextFilter);
    }

    history.replace(
      `${location.pathname}${nextSearch.toString() ? `?${nextSearch.toString()}` : ''}`,
    );
    setContentFilter(nextFilter);
    setPage(0);
  };

  const sortHandler = (
    _: any,
    option?: { label: string; value: string; order: SortType },
  ) => {
    if (!option) return;
    if (sortOption !== option.value) {
      setSort(option.order);
      setAllContentSortOptionAndType(option.order, option.value as SortOptions);
      setSortOption(option.value as SortOptions);
    } else if (sortOption === option.value) {
      const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
      setSort(sortT);
      setAllContentSortOptionAndType(sortT, option.value as SortOptions);
    }
    setPage(0);
  };

  const redirectToSharedPage = () => {
    history.push(PATHS.ALL_CONTENT.path);
  };

  const onBreadcrumbClick = (
    targetFolderId?: string,
    ancestorIndex?: number,
  ) => {
    if (!targetFolderId) {
      redirectToSharedPage();
      return;
    }

    setPage(0);

    const nextTrail =
      typeof ancestorIndex === 'number' && ancestorIndex > 0
        ? breadcrumbAncestorIds.slice(0, ancestorIndex).join(',')
        : '';

    history.push(
      `${PATHS.ALL_CONTENT_VIDEOFEEDS.path}?folderId=${targetFolderId}&parentPage=${PATHS.ALL_CONTENT.label}&page=0&limit=${limit}&sort=${sort}&search=${search}${
        nextTrail ? `&folderTrail=${encodeURIComponent(nextTrail)}` : ''
      }${
        contentFilter !== FOLDER_CONTENT_FILTER.ALL
          ? `&contentFilter=${contentFilter}`
          : ''
      }`,
    );
  };

  const onHierarchyBackClick = () => {
    const parentFolderId =
      breadcrumbAncestorIds[breadcrumbAncestorIds.length - 1];
    if (!parentFolderId) {
      return;
    }

    onBreadcrumbClick(
      String(parentFolderId),
      breadcrumbAncestorIds.length - 1,
    );
  };

  const onNameSort = () => {
    const sortT = sortOption === SortOptions.NAME && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setAllContentSortOptionAndType(sortT, SortOptions.NAME);
    setSortOption(SortOptions.NAME);
    setPage(0);
  };

  const onSizeSort = () => {
    const sortT = sortOption === SortOptions.SIZE && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setAllContentSortOptionAndType(sortT, SortOptions.SIZE);
    setSortOption(SortOptions.SIZE);
    setPage(0);
  };

  useEffect(() => {
    if (directAccess) {
      notification.showMessage('Oops.. seems the video was deleted');
    }
  }, [directAccess]);

  const onRowsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPage(0);
    setLimit(parseInt(e.target.value, 10));
  };

  const onPaginate = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const [showDeletedVideofeedsModal, setShowDeletedVidefeedsModal] =
    useState(false);
  const onTrashIconClick = () => {
    setShowDeletedVidefeedsModal(!showDeletedVideofeedsModal);
  };

  const videofeedsList = useMemo(
    () =>
      folderContents.filter(
        (item) => item.item_type !== 'folder',
      ) as VIDEOFEED_WITH_SIZE[],
    [folderContents],
  );

  const orderedFeedsForIndex = useMemo(() => {
    const ordered: VIDEOFEED_WITH_SIZE[] = [];
    const seen = new Set<string>();

    const append = (feed?: VIDEOFEED_WITH_SIZE) => {
      if (!feed) return;
      const id = String(feed.id);
      if (seen.has(id)) return;
      seen.add(id);
      ordered.push(feed);
    };

    videofeedsList.forEach((feed) => {
      append(feed);
      (feed.reference_timelines as VIDEOFEED_WITH_SIZE[] | undefined)?.forEach(
        (timeline) => append(timeline),
      );
    });

    return ordered;
  }, [videofeedsList]);

  const feedIndexById = useMemo(
    () =>
      orderedFeedsForIndex.reduce<Record<string, number>>((acc, feed, index) => {
        acc[String(feed.id)] = index;
        return acc;
      }, {}),
    [orderedFeedsForIndex],
  );

  const nextFolderTrail = useMemo(() => {
    const trailParts = folderTrail ? folderTrail.split(',').filter(Boolean) : [];
    if (folderId && trailParts[trailParts.length - 1] !== folderId) {
      trailParts.push(folderId);
    }

    return trailParts.join(',');
  }, [folderId, folderTrail]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.topBar}>
        <div className={styles.smallPath}>
          <span>File Management / </span>
          <button
            type="button"
            className={styles.breadcrumbButton}
            onClick={() => onBreadcrumbClick()}
          >
            All Content
          </button>
          {breadcrumbFolders.map((breadcrumbFolder, index) => (
            <React.Fragment key={breadcrumbFolder.id}>
              <span className={styles.breadcrumbSeparator}> / </span>
              <button
                type="button"
                className={styles.breadcrumbButton}
                onClick={() =>
                  onBreadcrumbClick(String(breadcrumbFolder.id), index)
                }
              >
                {breadcrumbFolder.name}
              </button>
            </React.Fragment>
          ))}
          {folder?.name && (
            <>
              <span className={styles.breadcrumbSeparator}> / </span>
              <span className={styles.breadcrumbCurrent}>{folder.name}</span>
            </>
          )}
        </div>
        <div className={styles.pageTitle}>
          {!!breadcrumbAncestorIds.length && (
            <ContainedButton
              kind="purple"
              className={styles.backButton}
              onClick={onHierarchyBackClick}
            >
              Back
            </ContainedButton>
          )}
          <span>{folder?.name}</span>
        </div>
      </div>
      {loading && <LinearProgress />}
      <div className={styles.searchWrapper}>
        <SearchInput quickFilter onChange={searchContent} className={styles.search} />
        <>
          <div className={styles.sortToolbarSelector}>
            <OrderSelector
              title={'Sorting by'}
              selectedValue={sortOption}
              selectedOrder={sort}
              options={[
                { label: 'A-Z ↓', value: SortOptions.NAME, order: SortType.ASC },
                { label: 'Z-A ↑', value: SortOptions.NAME, order: SortType.DESC },
                { label: 'Size ↓', value: SortOptions.SIZE, order: SortType.ASC },
                { label: 'Size ↑', value: SortOptions.SIZE, order: SortType.DESC },
                {
                  label: 'Last viewed ↑',
                  value: SortOptions.LAST_VIEWED,
                  order: SortType.ASC,
                },
                {
                  label: 'Last viewed ↓',
                  value: SortOptions.LAST_VIEWED,
                  order: SortType.DESC,
                },
                {
                  label: 'Last Modified ↑',
                  value: SortOptions.UPDATED_AT,
                  order: SortType.ASC,
                },
                {
                  label: 'Last Modified ↓',
                  value: SortOptions.UPDATED_AT,
                  order: SortType.DESC,
                },
                {
                  label: 'Created At ↑',
                  value: SortOptions.CREATED_AT,
                  order: SortType.ASC,
                },
                {
                  label: 'Created At ↓',
                  value: SortOptions.CREATED_AT,
                  order: SortType.DESC,
                },
              ]}
              onSelect={sortHandler}
            />
          </div>

          <div className={styles.sortToolbar2Selector}>
            <select
              id="filterBy"
              value={filterBy || ACCESS_TYPE_ENUM.ALL}
              disabled={contentFilter === FOLDER_CONTENT_FILTER.SUBFOLDER}
              onChange={(e) => sortFiltering(e.target.value as ACCESS_TYPE_ENUM)}
            >
              <option value={ACCESS_TYPE_ENUM.ALL}>All</option>
              <option value={ACCESS_TYPE_ENUM.VIEW}>View</option>
              <option value={ACCESS_TYPE_ENUM.VIEW_AND_COMMENT}>
                View & Comment
              </option>
              <option value={ACCESS_TYPE_ENUM.COMMENT_AND_CREATE}>
                Comment & Create
              </option>
              <option value={ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE}>
                Comment, Create & Reshare
              </option>
              <option value={ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE}>
                Edit & Reshare
              </option>
              <option value={ACCESS_TYPE_ENUM.WITHOUT_ACCESS}>Mixed</option>
            </select>
          </div>

          <div className={styles.filterToolbar}>
            <OrderSelector
              title={'Filter by'}
              selectedValue={contentFilter}
              selectedOrder={SortType.ASC}
              options={CONTENT_FILTER_OPTIONS}
              onSelect={onContentFilterSelect}
            />
          </div>

          <div className={styles.trashIcon} onClick={onTrashIconClick}>
            <TrashIcon />
          </div>
        </>
      </div>
      <AllContentVideofeedsHeader
        onDateSort={sortHandler}
        sort={sort}
        sortOption={sortOption}
        onNameClick={onNameSort}
        onSizeClick={onSizeSort}
      />
      <div className={styles.list}>
        {folderContents.map((item) =>
          item.item_type === 'folder' ? (
            <div key={`folder-${item.id}`} className={styles.folderRowWrapper}>
              <FolderContentRow
                folder={item}
                variant="all-content"
                dateSortOption={sortOption}
                onOpen={() => {
                  if (String(item.id) === String(folderId)) {
                    return;
                  }

                  history.push(
                    `${PATHS.ALL_CONTENT_VIDEOFEEDS.path}?folderId=${item.id}&parentPage=${PATHS.ALL_CONTENT.label}${
                      nextFolderTrail
                        ? `&folderTrail=${encodeURIComponent(nextFolderTrail)}`
                        : ''
                    }${
                      contentFilter !== FOLDER_CONTENT_FILTER.ALL
                        ? `&contentFilter=${contentFilter}`
                      : ''
                    }`,
                  );
                }}
                onShare={() => {
                  dispatch(selectShareFolder(item));
                  dispatch(switchShareModal(true));
                }}
              />
            </div>
          ) : (
            <AllContentVideo
              key={`feed-${item.id}`}
              feed={item}
              i={feedIndexById[String(item.id)] || 0}
              fetchDataCallback={fetchDataCallback}
              referenceTimelines={
                (item.reference_timelines as
                  | VIDEOFEED_WITH_SIZE[]
                  | undefined) || []
              }
              feedIndexById={feedIndexById}
              forceMovieIcon={(item.reference_timelines?.length || 0) > 0}
              dateSortOption={sortOption}
            />
          ),
        )}
      </div>
      <PaginationFooter
        hideVersion
        page={page}
        total={total}
        onPageChange={onPaginate}
        rowsPerPage={limit}
        onRowsPerPageChange={onRowsChange}
      />

      <DeletedVideofeedsModal
        open={showDeletedVideofeedsModal}
        switchModal={onTrashIconClick}
        onRestored={fetchDataCallback}
      />
      <ShareFolderModal defaultShareAccess={ACCESS_TYPE.COMMENT_AND_CREATE} setFetchTrigger={setFetchTrigger} allowCurrentUserShare />
      <VideofeedShareModal defaultShareAccess={ACCESS_TYPE.COMMENT_AND_CREATE} allowCurrentUserShare />
    </div>
  );
};

export default AllContentVideofeeds;
